import { createTheme, type MantineColorsTuple, type PopoverProps } from "@mantine/core";

import { GERMAN_DATE_FORMAT, parseGermanDate } from "./lib/date.js";

/**
 * mindsquare-Theme — Quelle: Design-System-Projekt „mindsquare Design System"
 * (claude.ai/design, generiert aus den 2026er-PowerPoint-Mastern).
 * Tokens: Orange #EA5B0B (Brand) · Yellow #FDC300 · Green #688E26 ·
 * Navy #1B365D · Ink #565656 · Logo-Grau #A8A8A7.
 * Radien und Schatten seit #362 weicher,
 * Schatten navy-getönt.
 *
 * Trick für die Bestands-UI: Die Mantine-NAMED-Farben blue/yellow/green
 * werden auf Brand-Rampen umdefiniert — damit rutschen alle bestehenden
 * `color="blue"`-Badges, Links und Aktiv-Zustände ohne Komponenten-Änderung
 * in die CI. `red` bleibt Mantine-Default (Fehler-Semantik).
 */

const msOrange: MantineColorsTuple = [
  "#fff4ec", // 0
  "#fdece0", // 1 — Token --ms-orange-soft
  "#fbd5bc",
  "#f8bc94",
  "#f49e66",
  "#f07c33",
  "#ea5b0b", // 6 — Brand --ms-orange
  "#cf5109", // 7 — Token --ms-orange-hover
  "#b34607",
  "#8f3805",
];

const msNavy: MantineColorsTuple = [
  "#eef1f5", // 0 — Token --ms-navy-soft
  "#dce2ea",
  "#c2ccd9",
  "#9faec4",
  "#7a8fac",
  "#536e92",
  "#1b365d", // 6 — Brand --ms-navy
  "#142a49", // 7 — Token --ms-navy-hover
  "#101f37",
  "#0a1526",
];

const msYellow: MantineColorsTuple = [
  "#fff9e6",
  "#fef2cc",
  "#fee599",
  "#fdd966",
  "#fdcd33",
  "#fdc814",
  "#fdc300", // 6 — Brand --ms-yellow
  "#e3ae00", // 7 — Token --ms-yellow-hover
  "#c29500",
  "#9e7a00",
];

const msGreen: MantineColorsTuple = [
  "#f2f7e9",
  "#e3eed2",
  "#c8dda6",
  "#accb79",
  "#91ba4d",
  "#7ca436",
  "#688e26", // 6 — Brand --ms-green
  "#587920", // 7 — Token --ms-green-hover
  "#48631a",
  "#374c14",
];

export const theme = createTheme({
  colors: {
    msOrange,
    msNavy,
    // Named-Farben auf Brand-Rampen mappen (siehe Kopfkommentar).
    blue: msNavy,
    yellow: msYellow,
    green: msGreen,
    orange: msOrange,
  },
  primaryColor: "msOrange",
  primaryShade: 6,

  fontFamily:
    "'Geist Variable', 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  headings: {
    fontFamily:
      "'Geist Variable', 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
    fontWeight: "600",
  },

  // Weichere Optik (#362): größere Radien, breite Schatten in Navy-Tönung.
  defaultRadius: "md",
  radius: {
    xs: "4px",
    sm: "6px",
    md: "10px",
    lg: "16px",
    xl: "20px",
  },

  shadows: {
    xs: "0 1px 2px rgba(27,54,93,.06)",
    sm: "0 1px 2px rgba(27,54,93,.06), 0 4px 12px rgba(27,54,93,.06)",
    md: "0 1px 2px rgba(27,54,93,.05), 0 10px 30px rgba(27,54,93,.07)",
    lg: "0 2px 4px rgba(27,54,93,.06), 0 16px 40px rgba(27,54,93,.10)",
    xl: "0 4px 8px rgba(27,54,93,.08), 0 24px 60px rgba(27,54,93,.14)",
  },

  components: {
    // Deutsche Datumsanzeige statt "Juli 23, 2026" (Mantine-Default-Format).
    DatePickerInput: { defaultProps: { valueFormat: GERMAN_DATE_FORMAT } },
    // Befund U5: `DateInput` ist das eine Steuerelement, das sich TIPPEN und
    // ANKLICKEN lässt. Der Parser steht hier statt an jedem Feld, damit jede
    // Tastatureingabe im ganzen Werkzeug gleich gelesen wird — Mantines
    // Rückfall (`dayjs` mit Formatstring) bräuchte das Plugin
    // `customParseFormat` und erkennt "30.06.2027" sonst gar nicht.
    DateInput: {
      defaultProps: { valueFormat: GERMAN_DATE_FORMAT, dateParser: parseGermanDate },
    },
  },
});

/**
 * Befund U6: Datumsauswahl, die IMMER nach unten aufklappt.
 *
 * Floating UIs `flip`-Middleware dreht ein Dropdown nach oben, sobald unter
 * dem Feld zu wenig Platz ist. In der Kundenakte sitzen die Datumsfelder
 * unter der Softwarestandstabelle — die Auswahl klappte damit ausgerechnet
 * über die Tabelle, gegen die man das Datum abgleicht. `shift` bleibt an,
 * damit die Auswahl seitlich im sichtbaren Bereich bleibt; nach unten wächst
 * notfalls die Seite mit.
 */
export const DATE_POPOVER_DOWNWARDS: Partial<Omit<PopoverProps, "children">> = {
  position: "bottom-start",
  middlewares: { flip: false, shift: true },
};

/**
 * Zentrale Chart-Farben (Recharts kennt keine Mantine-Variablen).
 * Konvention über alle Charts: Ist/primäre Reihe = Orange, Soll/Ziel = Navy,
 * Vorjahr/Referenz = Logo-Grau, positiv = Grün, Warnung/sekundär = Gelb.
 */
export const CHART_COLORS = {
  ist: "#ea5b0b",
  soll: "var(--msc-navy)",
  vorjahr: "#a8a8a7",
  positiv: "#688e26",
  warnung: "#fdc300",
  neutral: "#a8a8a7",
} as const;

/** BCG-Quadranten in Brand-Farben (konsistent zu QuadrantBadge-Semantik). */
export const BCG_QUADRANT_COLORS: Record<string, string> = {
  star: "#fdc300",
  cash_cow: "#688e26",
  question_mark: "var(--msc-bcg-question-mark)",
  dog: "var(--msc-bcg-dog)",
};

/** Stabile, unterscheidbare Palette für die Lead-Färbung der BCG-Blasen. */
export const LEAD_PALETTE = [
  "#ea5b0b", // Orange (Brand)
  "var(--msc-navy)", // Navy (im dunklen Modus aufgehellt, global.css)
  "#688e26", // Grün
  "#fdc300", // Gelb
  "#7a8fac", // Navy 4
  "#cf5109", // Orange dunkel
  "#91ba4d", // Grün hell
  "#536e92", // Navy 5
  "#f49e66", // Orange hell
  "#587920", // Grün dunkel
];
